import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { apiService } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Upload,
  FileText,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  Clock,
  X,
  Trash2,
} from "lucide-react";

const ImportCenterPage = () => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [batches, setBatches] = useState([]);

  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [step, setStep] = useState("upload"); // upload, preview, importing, complete
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      const res = await apiService.getImportBatches();
      setBatches(res.data);
    } catch (error) {
      console.error("Failed to fetch import batches:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file) => {
    const validExtensions = [".940", ".mt940", ".zip", ".csv"];
    const validMimeTypes = [
      "application/zip",
      "application/x-zip-compressed",
      "text/csv",
      "application/vnd.ms-excel",
    ];
    
    const hasValidExtension = validExtensions.some((ext) =>
      file.name.toLowerCase().endsWith(ext)
    );
    const hasValidMime = validMimeTypes.includes(file.type);

    if (!hasValidExtension && !hasValidMime) {
      toast.error("Please select a CSV (Wise/ING), MT940, or ZIP file");
      return;
    }

    setSelectedFile(file);
    handlePreview(file);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the drop zone entirely
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  const handlePreview = async (file) => {
    setStep("preview");
    try {
      const res = await apiService.previewImport(file);
      setPreview(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to preview file");
      setStep("upload");
      setSelectedFile(null);
    }
  };

  const handleConfirmImport = async () => {
    if (!selectedFile) return;

    setStep("importing");
    setImporting(true);

    try {
      const res = await apiService.confirmImport(selectedFile);
      toast.success(
        `Successfully imported ${res.data.imported} transactions`
      );
      setStep("complete");
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
      setStep("preview");
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setPreview(null);
    setStep("upload");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAmount = (amount, direction) => {
    const formatted = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
    return direction === "income" ? `+${formatted}` : `-${formatted}`;
  };

  return (
    <div className="space-y-6" data-testid="import-center-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Import Center</h1>
        <p className="text-slate-500 mt-1">
          Upload bank statements from Wise (CSV) or ING (MT940/CSV)
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upload Area */}
        <div className="lg:col-span-2">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Upload Statement</CardTitle>
            </CardHeader>
            <CardContent>
              {step === "upload" && (
                <div
                  className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors cursor-pointer ${
                    isDragging 
                      ? "border-emerald-500 bg-emerald-50" 
                      : "border-slate-200 hover:border-emerald-300"
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  data-testid="upload-zone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".940,.mt940,.zip,.csv,text/csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
                    isDragging ? "bg-emerald-200" : "bg-emerald-100"
                  }`}>
                    <Upload className={`w-8 h-8 text-emerald-600 ${isDragging ? "animate-bounce" : ""}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {isDragging ? "Release to upload" : "Drop your file here"}
                  </h3>
                  <p className="text-slate-500 mb-4">
                    or click to browse your computer
                  </p>
                  <div className="flex items-center justify-center gap-4 text-xs text-slate-400 flex-wrap">
                    <span className="flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-600 rounded">
                      <FileText className="w-4 h-4" />
                      Wise CSV
                    </span>
                    <span className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded">
                      <FileText className="w-4 h-4" />
                      ING MT940
                    </span>
                    <span className="flex items-center gap-1 px-2 py-1 bg-teal-50 text-teal-600 rounded">
                      <FileText className="w-4 h-4" />
                      ING CSV
                    </span>
                    <span className="flex items-center gap-1 px-2 py-1 bg-slate-100 rounded">
                      <FileText className="w-4 h-4" />
                      ZIP
                    </span>
                  </div>
                </div>
              )}

              {step === "preview" && preview && (
                <div className="space-y-6 animate-fade-in">
                  {/* File Info */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {selectedFile?.name}
                        </p>
                        <p className="text-sm text-slate-500">
                          {(selectedFile?.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleReset}
                      data-testid="clear-file"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>

                  {/* Summary */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="p-4 bg-emerald-50 rounded-xl text-center">
                      <p className="text-2xl font-bold text-emerald-600">
                        {preview.total_transactions}
                      </p>
                      <p className="text-sm text-emerald-700">Total Found</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-xl text-center">
                      <p className="text-2xl font-bold text-blue-600">
                        {preview.new_transactions}
                      </p>
                      <p className="text-sm text-blue-700">New</p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-xl text-center">
                      <p className="text-2xl font-bold text-amber-600">
                        {preview.duplicates}
                      </p>
                      <p className="text-sm text-amber-700">Duplicates</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-xl text-center">
                      <p className="text-2xl font-bold text-red-600">
                        {preview.invalid}
                      </p>
                      <p className="text-sm text-red-700">Invalid</p>
                    </div>
                  </div>

                  {/* Preview Table */}
                  {preview.transactions.length > 0 && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-3">
                        Preview (First {Math.min(preview.transactions.length, 10)}{" "}
                        transactions)
                      </h4>
                      <ScrollArea className="h-[300px] rounded-lg border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Date</TableHead>
                              <TableHead>Bank</TableHead>
                              <TableHead>Description</TableHead>
                              <TableHead className="text-right">Amount</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {preview.transactions.slice(0, 10).map((t, idx) => (
                              <TableRow key={idx}>
                                <TableCell className="text-sm">
                                  {new Date(
                                    t.transaction_date
                                  ).toLocaleDateString()}
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline">{t.bank}</Badge>
                                </TableCell>
                                <TableCell className="max-w-[200px] truncate text-sm">
                                  {t.counterparty_name ||
                                    t.description?.substring(0, 40) ||
                                    "Transaction"}
                                </TableCell>
                                <TableCell className="text-right">
                                  <span
                                    className={`font-medium ${
                                      t.direction === "income"
                                        ? "text-emerald-600"
                                        : "text-red-500"
                                    }`}
                                  >
                                    {formatAmount(t.amount, t.direction)}{" "}
                                    {t.currency}
                                  </span>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={handleReset}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleConfirmImport}
                      disabled={preview.new_transactions === 0}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="confirm-import"
                    >
                      Import {preview.new_transactions} Transactions
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {step === "importing" && (
                <div className="py-12 text-center animate-fade-in">
                  <Loader2 className="w-12 h-12 animate-spin text-emerald-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Importing Transactions
                  </h3>
                  <p className="text-slate-500">
                    Please wait while we process your file...
                  </p>
                </div>
              )}

              {step === "complete" && (
                <div className="py-12 text-center animate-fade-in">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Import Complete!
                  </h3>
                  <p className="text-slate-500 mb-6">
                    Your transactions have been successfully imported.
                  </p>
                  <Button
                    onClick={handleReset}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="import-another"
                  >
                    Import Another File
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Instructions */}
        <div>
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Supported Formats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-emerald-600" />
                  </div>
                  <h4 className="font-medium text-emerald-900">Wise (EUR/USD)</h4>
                </div>
                <p className="text-sm text-emerald-700">
                  Upload <strong>CSV</strong> statements from Wise to get full merchant names, card details, and transaction info.
                </p>
              </div>

              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                  <h4 className="font-medium text-blue-900">ING Netherlands (EUR)</h4>
                </div>
                <p className="text-sm text-blue-700">
                  Upload <strong>MT940</strong> (.940) or <strong>CSV</strong> files from ING with full counterparty information.
                </p>
              </div>

              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-slate-600" />
                  </div>
                  <h4 className="font-medium text-slate-900">ZIP Archives</h4>
                </div>
                <p className="text-sm text-slate-500">
                  ZIP files containing MT940 statements will be extracted and processed.
                </p>
              </div>

              <div className="p-4 border border-amber-200 bg-amber-50 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-900">
                      De-duplication
                    </h4>
                    <p className="text-sm text-amber-700 mt-1">
                      Duplicate transactions are automatically detected and
                      skipped based on date, amount, and reference.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Import History */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Import History</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : batches.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No imports yet</p>
            </div>
          ) : (
            <ScrollArea className="h-[300px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>File</TableHead>
                    <TableHead>Bank</TableHead>
                    <TableHead className="text-center">Imported</TableHead>
                    <TableHead className="text-center">Duplicates</TableHead>
                    <TableHead className="text-center">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.map((batch) => (
                    <TableRow key={batch.id}>
                      <TableCell className="text-sm">
                        {formatDate(batch.upload_date)}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {batch.filename}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{batch.source_bank}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-emerald-600 font-medium">
                          {batch.imported_count}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-amber-600 font-medium">
                          {batch.rejected_count}
                        </span>
                      </TableCell>
                      <TableCell className="text-center font-medium">
                        {batch.total_count}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ImportCenterPage;
